import { useState, useMemo } from "react";
import AppShell from "@/components/layout/AppShell";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import api from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ClockClockwise, MagnifyingGlass, ArrowClockwise } from "@phosphor-icons/react";
import TimelineView, { MiniStat, fmtNum } from "@/components/common/TimelineView";

export default function ReceivingTimelinePage() {
  const [q, setQ] = useState("");
  const [rcvs, setRcvs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [onlyRetur, setOnlyRetur] = useState(false);

  const load = () => {
    api.get("/receiving").then((r) => setRcvs(r.data)).catch(() => {});
  };
  useAutoRefresh(load, { interval: 0 });

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let base = rcvs;
    if (onlyRetur) base = base.filter((r) => r.is_retur);
    if (!term) return base.slice(0, 25);
    return base.filter((r) =>
      (r.ref_no || "").toLowerCase().includes(term) ||
      (r.supplier || "").toLowerCase().includes(term) ||
      (r.material || "").toLowerCase().includes(term) ||
      (r.truck_no || "").toLowerCase().includes(term) ||
      (r.spb_no || "").toLowerCase().includes(term)
    ).slice(0, 30);
  }, [q, rcvs, onlyRetur]);

  const openTimeline = async (r) => {
    setSelected(r);
    setLoading(true);
    setTimeline(null);
    try {
      const res = await api.get(`/receiving/${r.id}/timeline`);
      setTimeline(res.data);
    } catch (e) {
      toast.error("Gagal memuat timeline");
    } finally { setLoading(false); }
  };

  return (
    <AppShell
      title="Timeline Receiving"
      subtitle="Audit perjalanan supplier: Dibuat → Gate In → Timbang Masuk → Lab → SPB → Timbang Keluar → Selesai → Gate Out (Retur: skip Lab & SPB)"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
        {/* LEFT — search & pick */}
        <div className="rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 bg-slate-50 space-y-2">
            <div className="relative">
              <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="rcv-timeline-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cari No. Ref, supplier, material, plat, SPB…"
                className="h-10 pl-9 font-mono text-[12px]"
              />
            </div>
            <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="rcv-timeline-filter-retur"
                checked={onlyRetur}
                onChange={(e) => setOnlyRetur(e.target.checked)}
                className="accent-orange-500"
              />
              <ArrowClockwise size={12} weight="fill" className="text-orange-600" />
              Hanya tampilkan Retur
            </label>
            <p className="text-[10px] text-slate-500">
              {q || onlyRetur ? `${filtered.length} hasil` : `25 receiving terbaru dari total ${rcvs.length}`}
            </p>
          </div>
          <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-8 text-center text-xs text-slate-400">Tidak ada dokumen ditemukan.</div>
            ) : (
              filtered.map((r) => {
                const isActive = selected?.id === r.id;
                return (
                  <button
                    key={r.id}
                    onClick={() => openTimeline(r)}
                    data-testid={`rcv-timeline-pick-${r.ref_no}`}
                    className={`w-full text-left px-4 py-3 hover:bg-sky-50 transition-colors ${isActive ? "bg-sky-100 border-l-4 border-sky-500" : "border-l-4 border-transparent"}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono font-bold text-[12px] text-slate-800 truncate">{r.ref_no}</span>
                      {r.is_retur ? (
                        <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-orange-100 text-orange-800 border border-orange-300">RETUR</span>
                      ) : (
                        <span className="text-[9px] font-bold uppercase text-slate-500">{r.status || "-"}</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-600 mt-0.5 truncate">{r.supplier}</div>
                    <div className="text-[10px] text-slate-400 font-mono mt-0.5">{r.material || "-"} · {r.truck_no || "-"}</div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* RIGHT — timeline */}
        <div className="rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden">
          {!selected ? (
            <div className="py-24 text-center text-slate-400">
              <ClockClockwise size={40} weight="duotone" className="mx-auto text-slate-300 mb-3" />
              <div className="text-sm font-semibold text-slate-500">Pilih Dokumen Receiving di sebelah kiri</div>
              <div className="text-xs text-slate-400 mt-1">Timeline lengkap akan tampil di sini.</div>
            </div>
          ) : loading || !timeline ? (
            <div className="py-24 text-center text-slate-400 text-sm">Memuat timeline…</div>
          ) : (
            <RcvTimelineView data={timeline} />
          )}
        </div>
      </div>
    </AppShell>
  );
}

function RcvTimelineView({ data }) {
  const { receiving: r, events } = data;
  const isRetur = r.is_retur;
  const header = (
    <div className={`p-5 border-b border-slate-100 bg-gradient-to-r ${isRetur ? "from-orange-50" : "from-slate-50"} to-white`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className={`text-[10px] font-bold uppercase tracking-widest ${isRetur ? "text-orange-700" : "text-slate-500"} flex items-center gap-2`}>
            {isRetur && <ArrowClockwise size={11} weight="fill" />}
            Receiving {isRetur ? "· RETUR" : ""}
          </div>
          <div className="font-mono font-black text-xl text-slate-900 mt-0.5" data-testid="rcv-timeline-ref-no">{r.ref_no}</div>
          <div className="text-sm text-slate-700 mt-1">
            <span className="font-semibold">{r.supplier}</span>
            <span className="mx-2 text-slate-300">·</span>
            <span>{r.material}</span>
            {isRetur && r.retur_source && (
              <span className="ml-2 text-[10px] uppercase tracking-wide text-orange-700 font-bold">
                dari {r.retur_source}
              </span>
            )}
          </div>
          {isRetur && r.retur_do_no && (
            <div className="text-[11px] text-slate-500 mt-0.5 font-mono">Ref. DO: {r.retur_do_no}</div>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
          <MiniStat label="Kendaraan" value={r.truck_no || "-"} mono />
          <MiniStat label="Tank Tujuan" value={r.destination_tank || "-"} mono />
          <MiniStat label="Net (kg)" value={r.net_kg ? fmtNum(r.net_kg) : "—"} mono />
          <MiniStat label="Net (L)" value={r.net_liter ? fmtNum(r.net_liter) : "—"} mono />
        </div>
      </div>
    </div>
  );
  return <TimelineView header={header} events={events} />;
}
